import type { Process, ProcessState, Step } from "./types";

/**
 * The sample process for Marlow & Co. (Paper S1, 13f, CH12): the nine-step "Prepare supplier bills"
 * with its purchase-order group, as the studio boards draw it. It is in draft r4, started from v3,
 * which is live. The draft already holds one edit of yours and two proposals from Abacor's chat, so
 * the canvas opens on the states the boards show.
 */

const at = (now: Date, hour: number, minute: number) =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute);

const step = (
  id: string,
  title: string,
  after: readonly string[],
  answers: Step["answers"],
  rest: Pick<Step, "groupId" | "afterNote" | "from"> = {},
): Step => ({ id, title, after, answers, ...rest });

export function sampleProcesses(now: Date): Record<string, ProcessState> {
  const process: Process = {
    id: "prepare-supplier-bills",
    name: "Prepare supplier bills",
    owner: "Ada",
    liveVersion: 3,
    defaults: {
      doneWhen: "",
      each: "Once per bill",
      onlyWhen: "Every bill",
      who: "Ada",
      uses: "AP inbox · Xero read only",
      limits: "3 working days a case",
      ifItCant: "Stop and tell Maya.",
    },
    groups: [
      {
        id: "g-po",
        title: "Check against the purchase order",
        after: ["s01"],
        answers: { onlyWhen: "The supplier uses POs" },
      },
    ],
    steps: [
      step(
        "s01",
        "Read the invoice",
        [],
        { doneWhen: "Supplier, number, totals, GST and every line are found and agree." },
        { from: "Maya's walkthrough, 12:40" },
      ),
      step(
        "s02",
        "Match the supplier",
        ["s01"],
        {
          doneWhen: "Exactly one Xero contact has this ABN, or the supplier is new.",
          ifItCant: "Ask Maya, wait 2 days.",
        },
        { afterNote: "it uses the ABN", from: "Maya's walkthrough, 12:40" },
      ),
      step(
        "s03",
        "Check for duplicates",
        ["s02"],
        {
          doneWhen: "No earlier bill from this supplier has this number.",
          ifItCant: "Stop and tell Maya.",
        },
        { from: "Maya's walkthrough, 12:41" },
      ),
      step(
        "s04",
        "Code each line",
        ["s01"],
        { doneWhen: "The line has an account and a tax code.", each: "Line on the bill" },
        { from: "Maya's walkthrough, 12:44" },
      ),
      step(
        "s05",
        "Find the PO",
        [],
        {
          doneWhen: "One open PO matches the order number.",
          ifItCant: "Ask Maya, follow up in 3 days.",
        },
        { groupId: "g-po", from: "Maya's walkthrough, 12:47" },
      ),
      step(
        "s06",
        "Match each line",
        ["s05"],
        {
          doneWhen: "The line matches a PO line within 2%, or is marked missing.",
          each: "Line on the bill",
        },
        { groupId: "g-po", from: "Maya's walkthrough, 12:49" },
      ),
      step(
        "s07",
        "Approve large bills",
        ["s03", "s04", "s06"],
        {
          doneWhen: "Maya approves or declines creating the draft.",
          onlyWhen: "Over AUD 10,000",
          who: "Maya decides",
          limits: "2 working days",
        },
        { from: "Maya's walkthrough, 12:52" },
      ),
      step(
        "s08",
        "Create the draft in Xero",
        ["s07"],
        {
          doneWhen: "The draft exists and its total matches the invoice.",
          uses: "Xero · create drafts, never post or pay",
        },
        { from: "Maya's walkthrough, 12:55" },
      ),
      step(
        "s09",
        "Maya reviews",
        ["s08"],
        {
          doneWhen: "Maya accepts the draft, asks for a correction or rejects it.",
          who: "Maya decides",
        },
        { from: "Maya's walkthrough, 12:58" },
      ),
    ],
  };

  return {
    [process.id]: {
      process,
      draft: {
        rev: 4,
        changes: [
          {
            id: "change-1",
            at: at(now, 10, 12),
            by: "me",
            kind: "answer",
            stepId: "s02",
            key: "doneWhen",
            before: "Exactly one Xero contact has this ABN.",
            after: "Exactly one Xero contact has this ABN, or the supplier is new.",
          },
        ],
        proposals: [
          {
            id: "proposal-bank",
            at: at(now, 9, 24),
            kind: "new-step",
            from: "This chat",
            status: "pending",
            afterStepId: "s02",
            step: step(
              "bank-details",
              "Check the bank details",
              ["s02"],
              {
                doneWhen:
                  "The bank account on the invoice matches the one in Xero for this supplier.",
                ifItCant: "Stop and tell Maya. Never ask the supplier to confirm.",
              },
              { afterNote: "it uses the Xero contact", from: "This chat" },
            ),
          },
          {
            id: "proposal-tracking",
            at: at(now, 9, 33),
            kind: "answer",
            from: "This chat",
            status: "pending",
            stepId: "s04",
            key: "doneWhen",
            baseline: "The line has an account and a tax code.",
            value: "The line has an account, a tax code and a tracking category.",
          },
        ],
      },
    },
  };
}
